/**
 * Deal-document accept/reject permission gate.
 *
 * `<DocxEditor suggestionGate>` is consulted before a tracked change is accepted
 * or rejected (and to enable/disable the sidebar buttons). It hands us the
 * change's `author` string and the decision; we must answer allow/deny.
 *
 * PropDocs' rule lives in `decideSuggestion` (@propdocs-editor/review) and keys
 * off the suggestion's **status** and the **author's role** (creator | receiver |
 * lost) — notably: a removed ('lost') author's suggestion stays fully resolvable
 * by a present party, while a 'lost' actor can act on nothing.
 *
 * The relational `document_suggestions` rows are the source of truth for both
 * fields, so we resolve the change → its row → role/status. The link is the
 * `external_id` carried through OOXML in the author token (see ./authorToken):
 * `w:author` round-trips verbatim, so the id survives import/export with no
 * schema change. When a change carries no token (a Word-authored revision, or a
 * pre-token document), there is no row to consult — we fall back to the caller's
 * `unlinkedDecision`, which the container sets from the current user's own
 * ability to edit. Failing OPEN there matches CKEditor behavior for foreign
 * revisions; failing closed would strand un-resolvable changes in the document.
 */

import { decideSuggestion, type AuthorRole, type Suggestion } from '@propdocs-editor/review';
import { decodeAuthorToken } from './authorToken';

/** The subset of a `document_suggestions` row the gate needs. */
export interface DealSuggestionRow {
  externalId: string;
  status: string;
  authorRole: string;
}

export interface DealSuggestionGateInput {
  /** Rows from `getDocumentById.documentSuggestions`. */
  rows: readonly DealSuggestionRow[];
  /** Role of the user performing the action. */
  actorRole: AuthorRole;
  /** Id of the user performing the action. */
  actorId: string;
  /** Answer for a change with no linkable row (Word-authored / pre-token). */
  unlinkedDecision: boolean;
  /**
   * Once the document has been shared with a counterparty, nobody may accept
   * their OWN suggestion — that would let one side write into the agreement
   * unilaterally while wearing the appearance of review. Rejecting your own is
   * still allowed: withdrawing a proposal you made needs nobody's permission.
   *
   * Before the first share there is no counterparty to protect, and a creator
   * tidying their own draft must be able to resolve their own changes.
   */
  enforceNoSelfAccept: boolean;
}

/** What the gate answers: allow, plus why not when it denies. */
export interface DealSuggestionDecision {
  allowed: boolean;
  /** Short, user-facing explanation. Empty when allowed. */
  reason: string;
}

/** Shown on the disabled Accept button. */
export const SELF_ACCEPT_DENIED_REASON =
  'The other party must accept your own suggestions.';

const VALID_ROLES: ReadonlySet<string> = new Set(['creator', 'receiver', 'lost']);
const VALID_STATUSES: ReadonlySet<string> = new Set(['pending', 'accepted', 'rejected', 'deleted']);

function toAuthorRole(role: string | null | undefined): AuthorRole {
  return VALID_ROLES.has(role ?? '') ? (role as AuthorRole) : 'lost';
}

function toStatus(status: string | null | undefined): Suggestion['status'] {
  return VALID_STATUSES.has(status ?? '')
    ? (status as Suggestion['status'])
    : // An unrecognized status must not read as actionable.
      'accepted';
}

/**
 * Build the `suggestionGate` callback for `<DocxEditor>`.
 *
 * Pure + synchronous: the editor calls this during render to enable/disable
 * buttons, so it must not fetch. All inputs come from already-loaded query data.
 */
export function makeDealSuggestionGate({
  rows,
  actorRole,
  actorId,
  unlinkedDecision,
  enforceNoSelfAccept,
}: DealSuggestionGateInput): (input: {
  revisionId: number;
  author: string;
  decision: 'accept' | 'reject';
}) => DealSuggestionDecision {
  const byExternalId = new Map<string, DealSuggestionRow>();
  for (const row of rows) {
    if (row?.externalId) byExternalId.set(row.externalId, row);
  }

  return ({ author, decision }) => {
    // The token carries BOTH ids. `authorId` is what the self-accept rule needs,
    // and reading it from the token (rather than the row) also covers a change
    // whose relational row hasn't synced yet — the window in which someone could
    // otherwise accept their own brand-new suggestion.
    const { externalId, authorId } = decodeAuthorToken(author);

    if (
      enforceNoSelfAccept &&
      decision === 'accept' &&
      actorId &&
      authorId &&
      String(authorId) === String(actorId)
    ) {
      return { allowed: false, reason: SELF_ACCEPT_DENIED_REASON };
    }

    const row = externalId ? byExternalId.get(externalId) : undefined;

    // No token, or no matching row → nothing authoritative to evaluate.
    if (!row) return { allowed: unlinkedDecision, reason: unlinkedDecision ? '' : 'You cannot resolve this change.' };

    const suggestion: Suggestion = {
      externalId: row.externalId,
      // `kind` doesn't affect the accept/reject rule; carry a valid placeholder.
      kind: 'insertion',
      // Deliberately blank: the self-accept rule is applied ABOVE, from the
      // token, so it also covers a change whose row hasn't synced yet.
      // `decideSuggestion` has the same rule keyed on this field; feeding it
      // here too would run the check twice and let the two answers drift.
      // (It was hardcoded blank before as well — but with no check above it,
      // which is why the rule was silently dead for deal documents.)
      authorId: '',
      authorRole: toAuthorRole(row.authorRole),
      status: toStatus(row.status),
    };

    const verdict = decideSuggestion({ suggestion, actorRole, actorId, decision });
    return { allowed: verdict.allowed, reason: verdict.allowed ? '' : verdict.reason };
  };
}
